import { useClinicalEditors } from './context';
import { useEffect, useState } from 'react';
import { FileText, Sparkles } from 'lucide-react';
import { Button } from '@rudi/ui/button';
import { Checkbox } from '@rudi/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@rudi/ui/dialog';
import { Input } from '@rudi/ui/input';
import { Label } from '@rudi/ui/label';
import { Textarea } from '@rudi/ui/textarea';

import type {
  ClinicalTrialIntake,
  ClinicalTrialStudyDraft,
} from '@rudi/shared/types/clinical-trials';

interface StudyIntakeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: (intake: ClinicalTrialIntake) => void;
  investigatorSlug?: string | null;
}

const EMPTY_DRAFT: ClinicalTrialStudyDraft = {
  title: '',
  protocolNumber: '',
  studyType: 'interventional',
  recruitmentStatusCode: 'recruiting',
  publishToNetwork: true,
};

export function StudyIntakeDialog({
  open,
  onOpenChange,
  onComplete,
  investigatorSlug,
}: StudyIntakeDialogProps) {
  const {
    t,
    toast,
    operations: {
      analyzeStudyIntake,
      confirmStudyIntake,
      createStudyIntake,
      updateStudyIntakeDraft,
    },
  } = useClinicalEditors();
  const [draft, setDraft] = useState<ClinicalTrialStudyDraft>(EMPTY_DRAFT);
  const [file, setFile] = useState<File | null>(null);
  const [hint, setHint] = useState('');
  const [intake, setIntake] = useState<ClinicalTrialIntake | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setDraft(EMPTY_DRAFT);
      setFile(null);
      setHint('');
      setIntake(null);
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const update = <K extends keyof ClinicalTrialStudyDraft>(
    field: K,
    value: ClinicalTrialStudyDraft[K]
  ) => {
    setDraft(previous => ({ ...previous, [field]: value }));
  };

  const analyze = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await createStudyIntake(draft, file, hint, investigatorSlug);
      const analyzed = await analyzeStudyIntake(created.id);
      setIntake(analyzed);
      if (analyzed.draftData) {
        // The agent draft never carries the visibility checkbox — keep the
        // user's choice instead of silently resetting it.
        setDraft({
          ...analyzed.draftData,
          publishToNetwork: draft.publishToNetwork ?? true,
        });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!intake) return;
    setBusy(true);
    setError(null);
    try {
      await updateStudyIntakeDraft(intake.id, draft);
      const confirmed = await confirmStudyIntake(intake.id);
      onComplete?.(confirmed);
      toast({
        title: t('clinicalTrials.ui.studySubmittedForInclusion'),
        description: t('clinicalTrials.ui.theStudyHasBeenSubmittedAndWillAppearShortly'),
      });
      onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const reviewing = intake?.status === 'ready_for_review';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle>
            {reviewing
              ? t('clinicalTrials.ui.reviewAndConfirmStudy')
              : t('clinicalTrials.ui.addStudy')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {!reviewing && (
            <div className="space-y-2 rounded-3xl border p-4">
              <Label htmlFor="study-protocol-pdf">
                {t('clinicalTrials.ui.studyProtocolPdfOptional')}
              </Label>
              <Input
                id="study-protocol-pdf"
                type="file"
                accept="application/pdf,.pdf"
                onChange={event => setFile(event.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">
                {t('clinicalTrials.ui.thePdfStaysPrivateInRudiAndIsAnalysed')}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.title')}</Label>
              <Input value={draft.title} onChange={event => update('title', event.target.value)} />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.protocolNumber')}</Label>
              <Input
                value={draft.protocolNumber}
                onChange={event => update('protocolNumber', event.target.value)}
              />
            </div>
            <div>
              <Label>CTIS-ID</Label>
              <Input
                value={draft.ctisId ?? ''}
                onChange={event => update('ctisId', event.target.value)}
              />
            </div>
            <div>
              <Label>DRKS-ID</Label>
              <Input
                value={draft.drksId ?? ''}
                onChange={event => update('drksId', event.target.value)}
              />
            </div>
            <div>
              <Label>NCT-ID</Label>
              <Input
                value={draft.nctId ?? ''}
                onChange={event => update('nctId', event.target.value)}
              />
            </div>
            <div>
              <Label>Phase</Label>
              <Input
                value={draft.phase ?? ''}
                onChange={event => update('phase', event.target.value)}
              />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.therapeuticArea')}</Label>
              <Input
                value={draft.therapeuticArea ?? ''}
                onChange={event => update('therapeuticArea', event.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.indication')}</Label>
              <Input
                value={draft.indication ?? ''}
                onChange={event => update('indication', event.target.value || null)}
              />
            </div>
            <div className="col-span-2">
              <Label>Sponsor</Label>
              <Input
                value={draft.sponsor ?? ''}
                onChange={event => update('sponsor', event.target.value)}
              />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.protocolVersion')}</Label>
              <Input
                value={draft.protocolVersion ?? ''}
                onChange={event => update('protocolVersion', event.target.value)}
              />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.protocolDate')}</Label>
              <Input
                value={draft.protocolDate ?? ''}
                onChange={event => update('protocolDate', event.target.value)}
              />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.startDate')}</Label>
              <Input
                type="date"
                value={draft.startDate ?? ''}
                onChange={event => update('startDate', event.target.value || null)}
              />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.endDate')}</Label>
              <Input
                type="date"
                value={draft.endDate ?? ''}
                onChange={event => update('endDate', event.target.value || null)}
              />
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.recruitmentStatus')}</Label>
              <select
                className="h-10 w-full rounded-3xl border border-input bg-background px-3 text-sm"
                value={draft.recruitmentStatusCode ?? 'recruiting'}
                onChange={event => update('recruitmentStatusCode', event.target.value)}
              >
                <option value="not_started">{t('clinicalTrials.ui.notStarted')}</option>
                <option value="recruiting">{t('clinicalTrials.ui.recruiting')}</option>
                <option value="recruitment_paused">
                  {t('clinicalTrials.ui.recruitmentPaused')}
                </option>
                <option value="recruitment_ended">
                  {t('clinicalTrials.ui.recruitmentClosed')}
                </option>
              </select>
            </div>
            <div>
              <Label>{t('clinicalTrials.ui.plannedParticipantCount')}</Label>
              <Input
                type="number"
                min={0}
                value={draft.enrollmentTarget ?? ''}
                onChange={event =>
                  update('enrollmentTarget', event.target.value ? Number(event.target.value) : null)
                }
              />
            </div>
            <div className="col-span-2 flex items-start gap-2 rounded-3xl border p-3">
              <Checkbox
                id="study-publish-to-network"
                checked={draft.publishToNetwork ?? true}
                onCheckedChange={checked => update('publishToNetwork', checked === true)}
              />
              <div className="grid gap-0.5 leading-none">
                <Label htmlFor="study-publish-to-network" className="cursor-pointer">
                  {t('clinicalTrials.ui.addToStudyNetwork')}
                </Label>
                <p className="text-xs text-muted-foreground">
                  {t('clinicalTrials.ui.whenUncheckedTheStudyRemainsAvailableToTheChatbot')}
                </p>
              </div>
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.studyObjective')}</Label>
              <Textarea
                rows={4}
                value={draft.objective ?? ''}
                onChange={event => update('objective', event.target.value || null)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.investigationalProductsInterventions')}</Label>
              <Textarea
                rows={3}
                value={draft.products ?? ''}
                onChange={event => update('products', event.target.value || null)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.inclusionCriteria')}</Label>
              <Textarea
                rows={5}
                value={draft.inclusionCriteria ?? ''}
                onChange={event => update('inclusionCriteria', event.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.exclusionCriteria')}</Label>
              <Textarea
                rows={5}
                value={draft.exclusionCriteria ?? ''}
                onChange={event => update('exclusionCriteria', event.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.riskBenefitAssessment')}</Label>
              <Textarea
                rows={4}
                value={draft.riskBenefitSummary ?? ''}
                onChange={event => update('riskBenefitSummary', event.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label>{t('clinicalTrials.ui.additionalInformation')}</Label>
              <Textarea
                rows={4}
                value={draft.additionalInformation ?? ''}
                onChange={event => update('additionalInformation', event.target.value)}
              />
            </div>
          </div>

          {!reviewing && (
            <div>
              <Label>{t('clinicalTrials.ui.additionalInstructionsForTheRudiAgent')}</Label>
              <Textarea
                rows={3}
                value={hint}
                onChange={event => setHint(event.target.value)}
                placeholder={t(
                  'clinicalTrials.ui.optionalContextForInterpretationNotSentToThePlatform'
                )}
              />
            </div>
          )}

          {reviewing && intake.evidence.length > 0 && (
            <div className="rounded-3xl border p-4 space-y-2">
              <div className="flex items-center gap-2 font-medium">
                <FileText className="h-4 w-4" /> {t('clinicalTrials.ui.sourceReferences')}
              </div>
              {intake.evidence.slice(0, 12).map((item, index) => (
                <p
                  key={`${item.field ?? 'field'}-${index}`}
                  className="text-xs text-muted-foreground"
                >
                  <span className="font-medium text-foreground">
                    {item.field ?? t('clinicalTrials.ui.field')}
                  </span>
                  {item.page ? t('clinicalTrials.ui.pageValue1', { value1: item.page }) : ''}:{' '}
                  {item.shortExcerpt ?? '–'}
                </p>
              ))}
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('clinicalTrials.ui.cancel')}
          </Button>
          {reviewing ? (
            <Button
              type="button"
              onClick={confirm}
              disabled={busy || !draft.title || !draft.protocolNumber}
            >
              {busy ? t('clinicalTrials.ui.publishing') : t('clinicalTrials.ui.confirmAndPublish')}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={analyze}
              disabled={busy || (!file && (!draft.title || !draft.protocolNumber))}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {busy
                ? t('clinicalTrials.ui.rudiIsAnalysing')
                : file
                  ? t('clinicalTrials.ui.analyseWithRudi')
                  : t('clinicalTrials.ui.reviewEntries')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
